// THE RECENT FILES PAGE, as state (Figma 40014119:480632, Pouria 2026-10-14).
//
// Held in the store because three components with no relationship to each other read it: the page
// in the middle (components/SmartBox/RecentFilesPage.vue, mounted by the layout so it works over
// any page), the box's "Briefing Recent files" card that replaces the SmartBox while it is up
// (components/SmartBox/RecentFilesBox.vue), and the SmartBox itself, which opens it from the + menu
// and stages the picks on its Work-board when "← Back" is pressed. The same reason Home's opened
// Ongoing list lives in the briefing store (busy-my-web#630).
//
// The documents are the BFF's `documents` page — the same answer the small recent list and the
// Attachments briefing read — PAGE_SIZE at a time, with the next page read when the grid is
// scrolled to its end. Every rule about what a document LOOKS like is lib/recentFilesPage.js's.

import api from '@/lib/services/general.service'
import {
  PAGE_SIZE,
  parseRecentFilter,
  recentFileCards,
  recentFilterPills,
  serverFilterCounts,
  visibleCards,
  pickedCards,
  togglePick,
  restoredPage,
} from '@/lib/recentFilesPage'

const closed = () => ({
  open: false,
  scope: null,
  docs: [],
  page: 0,
  hasMore: false,
  status: 'idle', // 'idle' | 'loading' | 'ready' | 'error'
  filter: 'all',
  // What is typed in the box, and what was last SEARCHED (Search / Enter). The grid follows the
  // searched one (frames 43→44: typing leaves the grid alone, Search filters it).
  query: '',
  applied: '',
  // The server's whole-set pill counts (`documents/briefing`), or null until / unless it answers.
  totals: null,
  selected: [],
  view: 'grid',
})

export const state = closed()

export const getters = {
  open: s => !!s.open,
  status: s => s.status,
  loading: s => s.status === 'loading',
  failed: s => s.status === 'error',
  hasMore: s => !!s.hasMore,
  filter: s => s.filter,
  query: s => s.query,
  applied: s => s.applied,
  view: s => s.view,
  selected: s => s.selected,
  cards: s => recentFileCards(s.docs),
  pills: (s, g) => recentFilterPills(g.cards, s.filter, s.hasMore ? s.totals : null),
  visible: (s, g) => visibleCards(g.cards, { filter: s.filter, query: s.applied }),
  picked: (s, g) => pickedCards(g.cards, s.selected),
  isSelected: s => id => s.selected.includes(String(id || '')),
}

export const mutations = {
  RESET(s) { Object.assign(s, closed()) },
  SET(s, patch) { Object.assign(s, patch) },
  APPEND_DOCS(s, docs) {
    const seen = new Set(s.docs.map(d => String(d?.id || '')))
    s.docs = [...s.docs, ...(docs || []).filter(d => d && !seen.has(String(d.id || '')))]
  },
}

/** The BFF wraps replies as `{ data: { data } }` in some places and `{ data }` in others. */
const unwrap = (res) => res?.data?.data ?? res?.data ?? null

// Which open a read belongs to: a page closed (or re-opened) while a read was in flight must not
// have the old answer land in it.
let openSeq = 0

export const actions = {
  /**
   * Open the page. `scope` is the attachments grammar (`ongoing:<id>` inside a story, else home);
   * `query` pre-fills the name filter ("Find related files" on a highlighted sentence).
   */
  async open({ commit, dispatch }, { scope = null, query = '', restore = null } = {}) {
    openSeq += 1
    commit('RESET')
    const q = String(query || '').trim()
    // `restore` (lib/recentFilesPage `recentPageSnapshot`) is the page a file was viewed from, put
    // back by that file briefing's Close (busy-my-web#644): its scope, words, pill, view and picks.
    commit('SET', restore ? restoredPage(restore) : { open: true, scope: scope || null, query: q, applied: q })
    await Promise.all([dispatch('fetchPage', 1), dispatch('fetchTotals')])
  },

  /** The pills' whole-set counts. A failure keeps the loaded counts — the pills never go blank. */
  async fetchTotals({ commit, state: s }) {
    if (!s.open) return
    const seq = openSeq
    try {
      const res = await api.get('documents/briefing', s.scope ? { scope: s.scope } : {})
      if (!s.open || seq !== openSeq) return
      commit('SET', { totals: serverFilterCounts(unwrap(res)) })
    } catch (err) {
      console.error('[recent files] counts failed', err)
    }
  },

  /** One page of documents; page 1 replaces, later pages append. */
  async fetchPage({ commit, state: s }, page = 1) {
    if (!s.open) return
    const seq = openSeq
    commit('SET', { status: 'loading' })
    try {
      const request = { pageSize: PAGE_SIZE, page }
      if (s.scope) request.scope = s.scope
      const res = await api.get('documents', request)
      if (!s.open || seq !== openSeq) return
      const body = unwrap(res)
      const items = Array.isArray(body?.items) ? body.items : []
      if (page === 1) commit('SET', { docs: [] })
      commit('APPEND_DOCS', items)
      commit('SET', { page, hasMore: !!body?.hasNextPage, status: 'ready' })
    } catch (err) {
      console.error('[recent files] documents failed', err)
      if (s.open && seq === openSeq) commit('SET', { status: 'error' })
    }
  },

  /** The grid reached its end: the next page, when there is one and nothing is in flight. */
  async loadMore({ dispatch, state: s }) {
    if (!s.open || !s.hasMore || s.status === 'loading') return
    await dispatch('fetchPage', s.page + 1)
  },

  setFilter({ commit }, key) { commit('SET', { filter: parseRecentFilter(key) }) },
  setQuery({ commit }, q) { commit('SET', { query: String(q || '') }) },
  /** Search / Enter: the typed words become the grid's filter. */
  search({ commit, state: s }) {
    const q = String(s.query || '').trim()
    commit('SET', { query: q, applied: q })
  },
  /** The input's × and the no-results "Clear search": nothing typed, nothing filtered. */
  clearSearch({ commit }) { commit('SET', { query: '', applied: '' }) },
  /** The rail's "Clear selection" while the page is up. */
  clearSelection({ commit }) { commit('SET', { selected: [] }) },
  setView({ commit }, v) { commit('SET', { view: v === 'list' ? 'list' : 'grid' }) },
  toggleSelect({ commit, state: s }, id) { commit('SET', { selected: togglePick(s.selected, id) }) },

  /** Put the page away (Back stages the picks first — SmartBox — then calls this). */
  close({ commit, state: s }) {
    if (!s.open) return
    openSeq += 1
    commit('RESET')
  },
}
